// frontend/src/services/api.ts

// Se VITE_API_URL estiver vazio, usa URL relativa (para produção com nginx proxy)
// Caso contrário, usa a URL configurada (dev: http://localhost:3333)
const API_BASE_URL =
	import.meta.env.VITE_API_URL ||
	(import.meta.env.MODE === "production" ? "" : "http://localhost:3333");

interface ApiRequestOptions extends RequestInit {
	skipAuth?: boolean; // Para rotas públicas (login, register)
}

/**
 * Faz requisições HTTP com token JWT automaticamente incluído
 * @param endpoint - Endpoint da API (ex: "/api/patients")
 * @param options - Opções do fetch + skipAuth flag
 * @returns Promise com dados tipados
 */
export async function apiRequest<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
	const { skipAuth = false, ...fetchOptions } = options;
	const token = localStorage.getItem("token");

	// Montar headers
	const headers: HeadersInit = {
		"Content-Type": "application/json",
		...fetchOptions.headers,
	};

	// Adicionar token se não for rota pública
	if (!skipAuth && token) {
		headers.Authorization = `Bearer ${token}`;
	}

	// Fazer requisição
	const response = await fetch(`${API_BASE_URL}${endpoint}`, {
		...fetchOptions,
		headers,
	});

	// Tratar erro de autenticação (token expirado/inválido)
	if (response.status === 401) {
		localStorage.removeItem("token");
		localStorage.removeItem("user");
		window.location.href = "/login";
		throw new Error("Unauthorized: Token expired or invalid");
	}

	// Tratar outros erros HTTP
	if (!response.ok) {
		const errorData = await response.json().catch(() => ({}));
		throw new Error(errorData.error || `HTTP Error: ${response.statusText}`);
	}

	// Respostas sem corpo (204 No Content, ou Content-Length 0) não têm JSON para parsear
	if (response.status === 204 || response.headers.get("content-length") === "0") {
		return undefined as T;
	}
	const text = await response.text();
	return text ? (JSON.parse(text) as T) : (undefined as T);
}

// Funções helper para métodos HTTP comuns
export const api = {
	get: <T>(endpoint: string, options?: ApiRequestOptions) =>
		apiRequest<T>(endpoint, { ...options, method: "GET" }),

	post: <T>(endpoint: string, data?: unknown, options?: ApiRequestOptions) =>
		apiRequest<T>(endpoint, {
			...options,
			method: "POST",
			body: JSON.stringify(data),
		}),

	put: <T>(endpoint: string, data?: unknown, options?: ApiRequestOptions) =>
		apiRequest<T>(endpoint, {
			...options,
			method: "PUT",
			body: JSON.stringify(data),
		}),

	delete: <T>(endpoint: string, options?: ApiRequestOptions) =>
		apiRequest<T>(endpoint, { ...options, method: "DELETE" }),
};
